import React, { useEffect, useRef } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { useCookies } from 'next-client-cookies';
import Link from 'components/commercetools-ui/atoms/link';
import Breadcrumb from 'components/commercetools-ui/molecules/breadcrumb';
import { ChatBlackIcon, ChatIcon, ChatTootipIcon, ClockGrayIcon, EmailIcon, HelpIcon } from 'components/icons';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop } from 'helpers/utils/screensizes';
import { useRouter } from 'i18n/routing';
import { Category as CategoryFromEntity } from 'types/entity/category';
import { useAccount } from 'frontastic';
import useProjectSettings from 'frontastic/hooks/useProjectSettings';
import { EmailSupport, HelpCenter, LiveChat } from './types';
import { initIntercom } from '../../../../frontastic/lib/intercom';

export interface Props {
  data: any;
}

// The help centre is published per language, apart from these regional variants.
const HELP_CENTRE_REGIONAL_LOCALES = ['en-ca', 'en-gb', 'fr-ca'];

const getHelpCentreLocale = (locale: string) => {
  const normalised = locale.toLowerCase().replace('_', '-');

  return HELP_CENTRE_REGIONAL_LOCALES.includes(normalised) ? normalised : normalised.split('-')[0];
};

const ContactUsPage: React.FC<Props> = ({ data }) => {
  const { projectSettings } = useProjectSettings();
  const { intercomAppId } = projectSettings ?? {};
  const cookies = useCookies();
  const locale = (cookies.get('locale') as string) || 'en-us';
  const { account } = useAccount();
  const { email, addresses } = account || {};
  const [{ firstName } = {}] = addresses || [];
  const liveChatText = data?.liveChat[0] as LiveChat;
  const emailSupportText = data?.emailSupport?.[0] as EmailSupport;
  const helpCenterText = data?.helpCenter?.[0] as HelpCenter;

  const intercomId = intercomAppId;
  const [isDesktopSize] = useMediaQuery(desktop);
  // Live Chat is available 24/7 per business decision; enable/disable scheduling is a future enhancement, not implemented now.
  // TODO: `enabled` is kept (instead of inlining `true`) so the enable/disable scheduling can be reintroduced here later without touching the JSX below.
  const enabled = true;
  const router = useRouter();
  const baseUrl = data?.intercomUrl;
  const helpCentreLocale = getHelpCentreLocale(locale);
  const intercomConfiguredUrl = baseUrl ? `${baseUrl}/${helpCentreLocale}/` : '';

  useEffect(() => {
    if (typeof window !== 'undefined' && (window as any).Intercom) {
      (window as any).Intercom('boot', {
        app_id: intercomId,
        email: email,
        name: firstName || '',
      });
    } else {
      initIntercom(intercomId, email);
    }
  }, [intercomId, email, firstName]);

  const headingRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);
  if (!data) return null;

  const homeBreadCrumb = {
    name: data?.breadCrumbsHome || 'Home',
    categoryId: '_home',
    _url: '/',
  } as CategoryFromEntity;

  const handleIntercomEmailSupport = () => {
    router.push('/contact-us/email-us');
  };

  return (
    <div className="mx-20 sm:mx-24 md:mx-75 lg:mx-76 xl:mx-0">
      <div className="mx-auto max-w-screen-xl transition-all duration-300">
        <div className="lg:px-16 md:px-24">
          <Breadcrumb className="lg:px-20 sm:px-0" backCategory={homeBreadCrumb} listClassName="w-fit" ref={headingRef}>
            <Link
              link="/"
              className="max-w-170 truncate text-14 lg:max-w-250"
              data-ffid="lnk-breadcrumb-link-0"
              ref={headingRef}
            >
              <p title="Home" data-ffid="lbl-breadcrumb-home" className="text-14">
                {homeBreadCrumb?.name}
              </p>
            </Link>

            <p
              className="max-w-170 cursor-default truncate text-14 font-bold lg:max-w-250"
              title={data?.breadcrumbText}
              data-ffid="lbl-breadcrumb-title"
            >
              {isDesktopSize && data?.breadcrumbText} 
            </p>
          </Breadcrumb>
        </div>
      </div>
      <div className="container w-full" data-ffid="ctr-text-only-banner">
        <div className="pb-30 pt-10 text-center lg:pb-50">
          <h3 tabIndex={-1} className="pb-14 text-28 text-[#333333] lg:text-40" data-ffid="lbl-contactus-main-heading">
            {data?.mainHeading}
          </h3>
          <h4 className="text-20 text-[#5C5C5C] lg:text-28" data-ffid="lbl-contactus-sub-heading">
            {data?.subHeading}
          </h4>
        </div>
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-25 [grid-template-columns:repeat(auto-fit,minmax(280px,1fr))]">
            <div
              className={`flex h-full flex-col rounded-2xl border-2 ${enabled ? 'border border-[#F5F5F5] bg-[#F5F5F5] p-20 lg:p-24' : 'border border-[#D6D6D6] bg-[#D6D6D6] p-0'} `}
            >
              {!enabled && (
                <div
                  className="w-full rounded-t-2xl border-b-2 border-[#FCB02D] bg-[#FFFBEC] px-24 py-8"
                  data-ffid="icon-tootip"
                >
                  <p className="flex items-center text-14 text-[#333333]" data-ffid="icon-tootip">
                    <ChatTootipIcon data-ffid="icon-tootip" />
                    <span className="ml-8" data-ffid="lbl-tootip-text">
                      {liveChatText?.tooltipLabel}
                    </span>
                  </p>
                </div>
              )}
              <div className={`${enabled ? '' : 'p-20 lg:p-24'}`}>
                <div className={`flex items-start gap-20 lg:flex-col lg:gap-5`}>
                  <div
                    className="flex h-40 w-50 items-center justify-center rounded-lg bg-white lg:size-52"
                    data-ffid="lbl-chat-icon"
                  >
                    {enabled ? (
                      <ChatIcon dimension={isDesktopSize ? 32 : 30} data-ffid="lbl-chat-icon" />
                    ) : (
                      <ChatBlackIcon dimension={isDesktopSize ? 28 : 26} data-ffid="lbl-chat-icon" />
                    )}
                  </div>

                  <div>
                    <h3
                      className={`text-20 lg:text-28 ${enabled ? 'text-[#333333]' : 'text-[#5C5C5C]'}`}
                      data-ffid="lbl-chat-heading"
                    >
                      {liveChatText?.liveChatHeading}
                    </h3>
                    <p className={`flex items-center text-14 text-[#5C5C5C] lg:text-16`} data-ffid="lbl-chat-time">
                      <ClockGrayIcon data-ffid="lbl-clock-icon" />
                      <span className="ml-6" data-ffid="lbl-timezone">
                        {liveChatText?.timeTextLabel}
                      </span>
                    </p>
                    <p
                      className={`mt-12 pb-15 text-14 ${enabled ? 'text-[#333333]' : 'text-[#5C5C5C]'} md:mt-16 lg:pb-0 lg:text-16`}
                      data-ffid="lbl-chat-desc"
                    >
                      {liveChatText?.sortDescLabel}
                    </p>
                  </div>
                </div>
                <button
                  data-ffid="btn-live-chat-CTA"
                  onClick={() => (window as any).Intercom?.('show')}
                  disabled={!enabled}
                  type="button"
                  className={`mt-auto w-full rounded-full py-12 text-14 text-white lg:mt-24 lg:py-10 lg:text-16 ${
                    enabled ? 'bg-[#018463]' : 'cursor-not-allowed bg-[#5C5C5C]'
                  } `}
                >
                  {liveChatText?.startLiveChatbutton || 'Start Live Chat'}
                </button>
              </div>
            </div>

            <div className="flex h-full flex-col rounded-2xl border-2 border-[#F5F5F5] bg-[#F5F5F5] p-20 md:p-24">
              <div className={`flex items-start gap-20 lg:flex-col lg:gap-5`}>
                <div
                  className="flex h-40 w-50 items-center justify-center rounded-lg bg-white lg:size-52"
                  data-ffid="email-icon"
                >
                  <EmailIcon dimension={isDesktopSize ? 32 : 30} data-ffid="email-icon" />
                </div>

                <div>
                  <h3 className="text-20 text-[#333333] lg:text-28" data-ffid="lbl-email-heading">
                    {emailSupportText?.emailHeading}
                  </h3>
                  <p className="flex items-center text-14 text-[#5C5C5C] lg:text-16" data-ffid="lbl-email-responsetime">
                    <ClockGrayIcon data-ffid="lbl-clock1-icon" />
                    <span className="ml-6" data-ffid="lbl-response-text">
                      {emailSupportText?.responseTimeLabel}{' '}
                    </span>
                  </p>
                  <p
                    className={`mt-12 pb-15 text-14 text-[#333333] lg:pb-0 lg:text-16 ${enabled ? 'lg:mt-16' : 'lg:mt-24'}`}
                    data-ffid="lbl-email-desc"
                  >
                    {emailSupportText?.emailSortDesc}
                  </p>
                </div>
              </div>
              <button
                data-ffid="btn-email-CTA"
                type="button"
                onClick={handleIntercomEmailSupport}
                className={`mt-auto w-full rounded-full bg-[#018463] py-12 text-14 text-white lg:py-10 lg:text-16 ${enabled ? 'lg:mt-24' : 'lg:mt-54'}`}
              >
                {emailSupportText?.sendEmailCTA}
              </button>
            </div>

            <div className="flex h-full flex-col rounded-2xl border-2 border-[#F5F5F5] bg-[#F5F5F5] p-20 lg:p-24">
              <div className={`flex items-start gap-20 lg:flex-col lg:gap-5`}>
                <div
                  className="flex h-38 w-52 items-center justify-center rounded-lg bg-white lg:size-52"
                  data-ffid="helpcenter-icon"
                >
                  <HelpIcon dimension={isDesktopSize ? 32 : 24} data-ffid="helpcenter-icon" />
                </div>

                <div>
                  <h3 className="text-20 text-[#333333] lg:text-28" data-ffid="lbl-help-center-heading">
                    {helpCenterText?.helpCenterTitle}
                  </h3>
                  <p className="text-14 text-[#5C5C5C] lg:text-16" data-ffid="lbl-help-center-guide">
                    {helpCenterText?.browseGuides}
                  </p>
                  <p
                    className={`mt-12 pb-15 text-14 text-[#333333] lg:pb-0 lg:text-16 ${enabled ? 'lg:mt-16' : 'lg:mt-24'}`}
                    data-ffid="lbl-help-center-desc"
                  >
                    {helpCenterText?.helpCenterDesc}
                  </p>
                </div>
              </div>

              <a
                href={intercomConfiguredUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-ffid="btn-help-center-CTA"
                className={`mt-auto flex w-full items-center justify-center rounded-full bg-[#018463] py-12 text-14 text-white lg:py-10 lg:text-16 ${enabled ? 'lg:mt-24' : 'lg:mt-54'}`}
              >
                {helpCenterText?.browseHelpCenterCTA || 'Browse Help Center'}
                <ArrowTopRightOnSquareIcon className="ml-4 pb-2 size-20" aria-hidden="true" />
              </a>
            </div>
          </div>
          <p className="pb-25 pt-35 text-center text-sm text-gray-600" data-ffid="lbl-note">
            {data?.noteLabel}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContactUsPage;
